import { computed, effectScope, ref, watch, type EffectScope } from 'vue'
import { consola } from 'consola'
import { useBasketState } from './useBasketState'
import { useApi } from '@shared/api'
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useAuthState } from '~/entities/auth'

const log = consola.withTag('weekly-gift')

/** Прогресс гостя по механике «N заказов за неделю → подарок». */
export interface WeeklyGiftProgress {
  configured: boolean
  ordersDone: number
  ordersRequired: number
  minOrderAmount: number
  available: boolean
  alreadyReceived: boolean
  giftProductId: string | null
}

const EMPTY: WeeklyGiftProgress = {
  configured: false,
  ordersDone: 0,
  ordersRequired: 0,
  minOrderAmount: 0,
  available: false,
  alreadyReceived: false,
  giftProductId: null,
}

/**
 * Подарок за частоту заказов: «N заказов за календарную неделю».
 *
 * Механика независима от «подарка за сумму» (`useGiftTiers`). Пересекаются они
 * только в корзине: подарок в заказе один, и какой именно — выбирает гость,
 * положив нужный товар. Поэтому здесь нет авто-добавления: показываем предложение
 * и кладём подарок только по явному действию.
 */
function createWeeklyGift() {
  const api = useApi(true)
  const cartStore = useCartStore()
  const institution = useInstitutionStore()
  const basketState = useBasketState()
  const { isLoggedIn } = useAuthState()

  const progress = ref<WeeklyGiftProgress>({ ...EMPTY })
  const loading = ref(false)

  /** Подарочное блюдо целиком — нужно, чтобы положить его в корзину и показать название. */
  const giftProduct = computed(() => {
    const id = progress.value.giftProductId
    if (!id) return null

    for (const category of institution.products || []) {
      for (const product of (category as any).products || []) {
        if (String(product.id) === id) return product
        for (const variant of product.variants || product.products || []) {
          if (String(variant.id) === id) return variant
        }
      }
    }
    return null
  })

  /**
   * Владение подарком переживает перезагрузку.
   *
   * Сервер не хранит `isGift`/`giftSource`: после F5 корзина приезжает обычной позицией,
   * и механика не отличает выданный ею подарок от блюда, которое гость купил сам.
   * Тот же приём, что у подарка за сумму (`giftTierOwnership`).
   *
   * Хранение — localStorage, а НЕ sessionStorage: корзина живёт на сервере и переживает
   * закрытие вкладки, а запись о владении в sessionStorage — нет. Гость забирал подарок,
   * возвращался на следующий день — и подарок в корзине переставал быть подарком:
   * скидка обнулялась, а кнопка «Добавить подарок» предлагала положить его второй раз.
   */
  const OWNERSHIP_KEY = 'weeklyGiftOwnership'

  function readOwnedProductId(): string | null {
    if (!import.meta.client) return null
    try {
      return localStorage.getItem(OWNERSHIP_KEY)
    } catch {
      return null
    }
  }

  function writeOwnedProductId(productId: string | null): void {
    if (!import.meta.client) return
    try {
      if (productId === null) localStorage.removeItem(OWNERSHIP_KEY)
      else localStorage.setItem(OWNERSHIP_KEY, productId)
    } catch {
      // Приватный режим — переживём без персистентности
    }
  }

  const ownedProductId = ref<string | null>(readOwnedProductId())

  /**
   * Позиция корзины, выданная как подарок ЭТОЙ механикой (или null).
   *
   * Сравнивать по одному `productId` нельзя: гость мог купить то же блюдо за деньги —
   * тогда кнопка «Добавить подарок» пропадала, и забрать бесплатное было невозможно.
   */
  const giftLine = computed<{ price: number } | null>(() => {
    const id = progress.value.giftProductId
    if (!id) return null

    const line: any = cartStore.cart.find((item: any) => {
      const sameProduct = String(item.productId ?? item.id) === id
      if (!sameProduct) return false
      // До перезагрузки — по флагам механики, после — по сохранённому владению.
      return (item.isGift && item.giftSource === 'weekly') || ownedProductId.value === id
    })

    return line ? { price: Math.max(0, Number(line.price) || 0) } : null
  })

  /** Подарок уже лежит в корзине — предлагать второй раз не нужно. */
  const isGiftInCart = computed(() => giftLine.value !== null)

  /**
   * Скидка за подарок: цена ОДНОЙ единицы подарочного блюда в корзине.
   *
   * Подарок лежит в корзине по полной цене (так его видит касса), поэтому эта величина
   * обязана вычитаться из итога и из базы сервисного сбора. Без неё в фолбэк-расчёте
   * (preview с бэка недоступен) строка показывала «Бесплатно», а платил гость полностью.
   */
  const giftDiscount = computed(() => (progress.value.alreadyReceived ? 0 : giftLine.value?.price ?? 0))

  /** Показывать блок: механика настроена и гость к ней хоть как-то причастен. */
  const shouldShow = computed(
    () => progress.value.configured && !progress.value.alreadyReceived && Boolean(giftProduct.value),
  )

  /** Сколько заказов осталось до подарка. */
  const ordersLeft = computed(() =>
    Math.max(0, progress.value.ordersRequired - progress.value.ordersDone),
  )

  async function load(): Promise<void> {
    const branchId = institution.selectedBranch?.id
    if (!import.meta.client || !isLoggedIn.value || !branchId) {
      progress.value = { ...EMPTY }
      return
    }

    loading.value = true
    try {
      const { data } = await api.get<WeeklyGiftProgress>(`/weekly-gift/progress/${branchId}`)
      progress.value = data ?? { ...EMPTY }
    } catch (error) {
      // Механика необязательная: молча гасим, корзина не должна ломаться из-за неё.
      log.warn('Не удалось получить прогресс подарка за частоту', error)
      progress.value = { ...EMPTY }
    } finally {
      loading.value = false
    }
  }

  /** Положить подарок в корзину по явному выбору гостя. */
  async function addGiftToCart(): Promise<void> {
    const product = giftProduct.value
    const branchId = institution.selectedBranch?.id
    if (!product || !branchId || !progress.value.available || isGiftInCart.value) return

    log.info('Кладём подарок за частоту в корзину:', product.name)

    try {
      // ⚠️ Филиал — обязательный ВТОРОЙ аргумент addToCart. Без него позиция
      // не доезжает до корзины, и кнопка «Добавить подарок» молча ничего не делает.
      await cartStore.addToCart(
        {
          id: String(product.id),
          name: product.name,
          price: Number(product.price) || 0,
          img: product.img || [],
          category_id: product.category_id,
          itemType: 'product',
          productId: String(product.id),
          isGift: true,
          giftSource: 'weekly',
          quantity: 1,
        } as any,
        Number(branchId),
      )
    } catch (error) {
      // Сеть или лимиты филиала: владение не отмечаем — подарка в корзине нет.
      log.warn('Не удалось добавить подарок за частоту в корзину', error)
      return
    }

    ownedProductId.value = String(product.id)
    writeOwnedProductId(ownedProductId.value)
  }

  // Скидка живёт в состоянии корзины: её потребляют сервисный сбор, итог и детализация.
  watch(
    giftDiscount,
    (discount) => {
      basketState.setWeeklyGiftDiscount(discount)
    },
    { immediate: true },
  )

  // Подарок ушёл из корзины (гость удалил или заказ оформлен) — владение снимаем,
  // иначе после F5 «чужая» платная позиция считалась бы нашим подарком.
  watch(isGiftInCart, (inCart) => {
    // Пока корзина переезжает на другой филиал, её состав ещё от прежнего:
    // подарок «исчезает» на мгновение, и без этой проверки мы сняли бы владение,
    // а вернувшаяся позиция стала бы платной.
    if (!cartStore.isSettledForBranch) return

    if (!inCart && ownedProductId.value !== null) {
      ownedProductId.value = null
      writeOwnedProductId(null)
    }
  })

  return {
    progress,
    loading,
    giftProduct,
    giftLine,
    giftDiscount,
    isGiftInCart,
    shouldShow,
    ordersLeft,
    load,
    addGiftToCart,
  }
}

// Синглтон в отдельном scope — как у подарка за сумму: механика живёт независимо от
// жизненного цикла компонентов. Иначе `watch` умирает вместе с первой размонтированной
// корзиной, и скидка застревает на последнем значении; заодно мобильная и десктопная
// корзины делят один экземпляр и не расходятся в состоянии.
let scope: EffectScope | null = null
let instance: ReturnType<typeof createWeeklyGift> | null = null

export function useWeeklyGift(): ReturnType<typeof createWeeklyGift> {
  if (instance) return instance
  scope = effectScope(true)
  instance = scope.run(() => createWeeklyGift()) as ReturnType<typeof createWeeklyGift>
  return instance
}

/** Только для тестов: сбрасывает синглтон между кейсами. */
export function __resetWeeklyGiftForTests(): void {
  scope?.stop()
  scope = null
  instance = null
}
